/**
 * Tab-lifetime caches for reference data every screen asks for again: field
 * metadata (`/metadata?table_name=`) and the lookup lists behind id -> name
 * labels (brokers, landmen, AOIs, entities). The deals page alone asked for
 * seven metadata tables on every visit, at a 2.5 s median each (TEST App
 * Insights, 90 days).
 *
 * Freshness:
 *   - Any write through this app's services to those tables clears the
 *     matching cache (see the write functions in dealsService, brokerService,
 *     metadataAdminService), so the person who made the change sees it at once.
 *   - Entries expire after REFERENCE_TTL_MS, which bounds how long ANOTHER
 *     user's change can stay unseen in a tab that is left open.
 *
 * Keys always carry the caller's user oid: de-hub narrows metadata per role
 * (external brokers get fewer fields), so one user's answer is never served
 * to another account signed in to the same tab.
 *
 * Only `response.data` is kept, and every hit gets its own structuredClone,
 * so a caller that sorts or edits what it was given cannot change what the
 * next caller sees.
 */
import { sessionCache } from "../utils/sessionCache";

export const REFERENCE_TTL_MS = 10 * 60 * 1000;

export const metadataCache = sessionCache();
export const lookupCache = sessionCache();

/**
 * `call()`'s response for `key`, from `cache` while fresh. In-flight requests
 * are shared; a failed request is not kept, so the next call retries.
 *
 * @param {Map} cache
 * @param {string} key
 * @param {() => Promise<{ data: unknown }>} call
 * @returns {Promise<{ data: unknown }>}
 */
export const cachedResponse = (cache, key, call) => {
  const hit = cache.get(key);
  let promise = hit && Date.now() - hit.at < REFERENCE_TTL_MS ? hit.promise : null;
  if (!promise) {
    promise = call().then((resp) => resp?.data);
    const entry = { promise, at: Date.now() };
    cache.set(key, entry);
    promise.catch(() => {
      if (cache.get(key) === entry) cache.delete(key);
    });
  }
  return promise.then((data) => ({ data: structuredClone(data) }));
};

/**
 * Runs a write and empties `cache` once it settles, success or not: a failed
 * request may still have changed the row server-side.
 *
 * @template T
 * @param {Map} cache
 * @param {Promise<T>} request
 * @returns {Promise<T>}
 */
export const clearingAfter = (cache, request) =>
  request.finally(() => cache.clear());
